import { InMemoryWebStorage, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { sessionTokenProvider } from "./sessionToken";

export interface AuthConfig { issuer: string; client_id: string; domain: string; callback_origin?: string; company_hall_id?: string; company_slug?: string }
let managerPromise: Promise<UserManager> | undefined;
let configuration: AuthConfig | undefined;

export function validateConfig(value: AuthConfig): AuthConfig {
  if (!/^https:\/\/cognito-idp\.us-east-1\.amazonaws\.com\/us-east-1_[A-Za-z0-9]+$/.test(value.issuer)
    || !/^[A-Za-z0-9]{8,128}$/.test(value.client_id)
    || !/^https:\/\/[a-z0-9-]+\.auth\.us-east-1\.amazoncognito\.com$/.test(value.domain)) {
    throw new Error("Staff authentication configuration is unavailable.");
  }
  if (value.callback_origin !== undefined && (value.callback_origin !== "https://fivecrownsystem.com"
    || !value.company_hall_id || !value.company_slug
    || !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value.company_slug))) {
    throw new Error("Company sign-in routing is unavailable.");
  }
  return value;
}

export function companySite(): Pick<AuthConfig, "company_hall_id" | "company_slug"> | undefined {
  return configuration?.company_hall_id ? configuration : undefined;
}

async function manager(): Promise<UserManager> {
  managerPromise ??= (async () => {
    const response = await fetch("/api/auth/config", { cache: "no-store", credentials: "omit" });
    if (!response.ok) throw new Error("Staff authentication is not configured.");
    configuration = validateConfig(await response.json() as AuthConfig);
    if (configuration.company_slug && window.location.hostname !== `${configuration.company_slug}.fivecrownsystem.com`) {
      throw new Error("Company sign-in host does not match its server binding.");
    }
    return new UserManager({
      authority: configuration.issuer,
      client_id: configuration.client_id,
      redirect_uri: `${configuration.callback_origin ?? window.location.origin}/auth/callback`,
      response_type: "code",
      scope: "openid email profile",
      disablePKCE: false,
      // Keep only pending OAuth state in sessionStorage; credentials remain memory-only.
      stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
      userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
      automaticSilentRenew: false,
      loadUserInfo: false,
      revokeTokenTypes: ["refresh_token"],
    });
  })().catch((error: unknown) => { managerPromise = undefined; throw error; });
  return managerPromise;
}

// One initialization promise also prevents React StrictMode from consuming a code twice.
let initialization: Promise<boolean> | undefined;
export function initializeAuthentication(): Promise<boolean> {
  initialization ??= (async () => {
    const client = await manager();
    if (window.location.pathname === "/auth/callback") {
      let destination = "/#/company";
      try {
        const user = await client.signinRedirectCallback();
        const requested = (user.state as { entry?: unknown } | undefined)?.entry;
        if (requested === "owner") destination = "/#/owner";
      }
      finally { window.history.replaceState(null, "", destination); }
    }
    const user = await client.getUser();
    return !!user && !user.expired;
  })().catch((error: unknown) => { initialization = undefined; throw error; });
  return initialization;
}

export async function signIn(): Promise<void> {
  const client = await manager();
  await client.signinRedirect({ url_state: configuration?.company_slug, state: { entry: !configuration?.company_slug && window.location.hash.startsWith("#/owner") ? "owner" : "company" } });
}

export const accessToken = sessionTokenProvider(manager);

export async function signOut(): Promise<void> {
  const client = await manager();
  try { await client.revokeTokens(["refresh_token"]); }
  finally {
    await client.removeUser();
    initialization = undefined;
    const url = new URL(`${configuration!.domain}/logout`);
    url.searchParams.set("client_id", configuration!.client_id);
    url.searchParams.set("logout_uri", `${configuration!.callback_origin ?? window.location.origin}/`);
    window.location.assign(url.href);
  }
}
